import {Browser, browserFromBackendObject, browserSortingFunction} from '../browser';
import {
    DeviceProperties,
    DevicePropertyName,
    DevicePropertyNames, DeviceRequirements
} from '@webmate-web-core/shared/types/devices/device-properties';
import {DeviceSlotId} from "@webmate-web-core/shared/types/ids";
import {isDefined} from "../../utils/utils";
import {Resolution} from '@webmate-web-core/shared/types/resolution';
import {Platform, platformComparator, platformFromString} from '@webmate-web-core/shared/types/platform';

export type DeviceOfferId = string;

export class Deployable {
    constructor(public readonly id: DeviceOfferId, public readonly modelName: string, public readonly platform: Platform, public readonly browser: Browser[], public readonly properties: DeviceProperties, public readonly runtimeCost: number) {}
}

export class DeployableGroup {
    constructor(public readonly id: string, public readonly groupBy: string, public readonly deployables: Deployable[]) {}
}

export class AdvertisedDeployable {
    constructor(public readonly displayName: string, public readonly logo: string, public platformLogo?: string) {}
}

export class DeviceSlotTemplate {
    constructor(public readonly name: string, public readonly id: string, public readonly fields: DeviceSlotTemplateField[], public readonly partialSlotDefinition: any) {}
}

export class DeviceSlotTemplateField {
    constructor(public readonly fieldType: string, public readonly jsonPaths: string[], public readonly description?: string, public readonly displayName?: string) {}
}

export function deployableFromBackendObject(value: any): Deployable {
    let browsers = (value.deviceProperties[DevicePropertyNames.MachineBrowsers] as any[]).map(browserFromBackendObject).sort(browserSortingFunction);
    let platform = platformFromString(value.deviceProperties[DevicePropertyNames.MachinePlatform]);
    let modelName = value.deviceProperties[DevicePropertyNames.ModelName];

    return new Deployable(value.id, modelName, platform, browsers, value.deviceProperties as DeviceProperties, value.runtimeCost);
}

export function slotTemplateFromBackendObject(value: any): DeviceSlotTemplate {
    let fields: DeviceSlotTemplateField[] = value.fields.map(field => new DeviceSlotTemplateField(field.fieldType, field.jsonPaths, field.meta.description, field.meta.displayName));

    return new DeviceSlotTemplate(value.name, value.id, fields, value.partialSlotDefinition);
}

export function anyAvailable(deployableGroup: DeployableGroup): boolean {
    return deployableGroup.deployables.some(depl => isAvailable(depl) && !isDefect(depl));
}


export function isAvailable(deployable: Deployable): boolean {
    return (deployable.properties[DevicePropertyNames.Unavailable] ?? false) !== true;
}

/**
 * Deployables offered by a headless slot. They have no meaningful platform, model or console and are
 * therefore not offered for interactive device selection.
 */
export function isHeadlessDeployable(deployable: Deployable): boolean {
    return deployable.properties[DevicePropertyNames.ProviderType] === 'headless';
}

export function isDefect(deployable: Deployable): boolean {
    let annotations = deployable.properties[DevicePropertyNames.Annotations];
    return annotations && annotations['status'] === 'defect';
}

export function constructRequirementsForDeployableInSlot(deployable: Deployable, slotId: DeviceSlotId, allowJobs: boolean, resolution?: Resolution): DeviceRequirements {
    const requirements = constructRequirementsForDeployable(deployable, allowJobs, resolution);
    return {
        ...requirements,
        [DevicePropertyNames.SlotId]: slotId
    };
}

export function constructRequirementsForDeployable(deployable: Deployable, allowJobs: boolean, resolution?: Resolution): DeviceRequirements {
    let requirements: DeviceRequirements = {
        [DevicePropertyNames.DeviceOfferId]: deployable.id
    };

    if (resolution !== undefined) {
        requirements = {
            ...requirements,
            [DevicePropertyNames.Resolution]: resolution
        };
    }

    if (allowJobs) {
        requirements = {
            ...requirements,
            [DevicePropertyNames.AutomationAvailable]: allowJobs
        };
    }
    return requirements;
}

export function deployableSortingFunction(a: Deployable, b: Deployable) {
    if (isDefined(a.modelName) && isDefined(b.modelName)) {
        //separate different types of platforms
        if (a.platform.platformType !== b.platform.platformType) {
            return a.platform.platformType.localeCompare(b.platform.platformType)
        }

        //sort by manufacturer
        const manufacturerA = a.properties[DevicePropertyNames.ManufacturerName];
        const manufacturerB = b.properties[DevicePropertyNames.ManufacturerName];
        if (isDefined(manufacturerA) && isDefined(manufacturerB) && manufacturerA !== manufacturerB) {
            return manufacturerA.localeCompare(manufacturerB);
        }

        //sort by model
        if (a.modelName !== b.modelName) {
            return a.modelName.localeCompare(b.modelName);
        }

        //sort by platform for identical models
        return platformComparator(a.platform, b.platform);
    } else {
        return platformComparator(a.platform, b.platform);
    }
}
